/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";

import { useTranslation } from "react-i18next";

import Typography from "@mui/material/Typography";

import { colors, InfoIcon } from "@dotnet/telematics-design-system";

import { CombinedDataTypes, VehicleDataTypes } from "../../../shared";
import Checkbox from "../../CheckBox/Checkbox";
import Tooltip from "../../Tooltip/Tooltip";
import { useStyles } from "../styles";
import {
  REPEAT_NAME_VALUES,
  REPEAT_TYPE_VALUES,
  SCHEDULE_PAYLOAD_NAMES,
  ScheduleRepeatPeriod
} from "../types";

interface DownloadDataTypesCheckboxesProps {
  selectedDataTypes: CombinedDataTypes[];
  handleChange: (name: SCHEDULE_PAYLOAD_NAMES, value: VehicleDataTypes) => void;
  repeatValue?: ScheduleRepeatPeriod | null;
  isLoading?: boolean;
}

const DownloadDataTypesCheckboxes = ({
  selectedDataTypes,
  handleChange,
  repeatValue,
  isLoading
}: DownloadDataTypesCheckboxesProps) => {
  const { t } = useTranslation();

  const { infoIconCSS, typographyTitleIconCSS } = useStyles();

  const getSubtitleText = (value: VehicleDataTypes) => {
    const repeatDays = Number(repeatValue?.days);

    const getRepeatType = () => {
      switch (repeatDays) {
        case -1:
          return REPEAT_TYPE_VALUES.Hourly;
        case 1:
          return REPEAT_TYPE_VALUES.Single;
        default:
          return REPEAT_TYPE_VALUES.Multiple;
      }
    };

    if (
      value === VehicleDataTypes.Activities &&
      repeatValue?.name === REPEAT_NAME_VALUES.Monthly
    ) {
      return t(
        "Schedules##vehicle schedules##download data activities tooltip monthly"
      );
    }

    return value === VehicleDataTypes.Activities &&
      !Number.isNaN(repeatDays) &&
      Boolean(repeatDays)
      ? t(
          `Schedules##vehicle schedules##download data activities tooltip ${getRepeatType()}`,
          { repeatDays: repeatValue?.days }
        )
      : undefined;
  };

  return (
    <div data-testid="download-data-types-checkboxes">
      <div css={css(typographyTitleIconCSS)}>
        <Typography variant="body2Medium" sx={{ color: colors.textSecondary }}>
          {t("Dialog##download data types")}
        </Typography>

        <Tooltip
          title={t(`Schedules##download activities##tooltip text`)}
          size="medium"
          placement="top"
          hasArrow
        >
          <span>
            <InfoIcon
              data-testid="data-types-checkboxes-info-icon"
              height={14}
              width={14}
              css={css(infoIconCSS)}
            />
          </span>
        </Tooltip>
      </div>
      {Object.values(VehicleDataTypes).map(value => (
        <div key={`schedule_${value}`}>
          <Checkbox
            id={`schedule_${value}`}
            checked={selectedDataTypes.includes(value)}
            disabled={value === VehicleDataTypes.Overview || isLoading}
            onChange={() =>
              handleChange(SCHEDULE_PAYLOAD_NAMES.FileTypes, value)
            }
            label={t(`Schedules##file types##${value}`)}
            info={getSubtitleText(value)}
            // Add margin bottom to the last checkbox so that it does not jump when changing date in date picker
            customStyle={{
              marginBottom:
                value === VehicleDataTypes.Activities && !getSubtitleText(value)
                  ? "12px"
                  : "0"
            }}
          />
        </div>
      ))}
    </div>
  );
};

export default DownloadDataTypesCheckboxes;
